import { ITreeNodeDTO } from 'business/models/Models';
import { EExtraFieldNames } from 'enums/EExtraFieldNames';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';

interface LocationTreeNode extends ITreeNodeDTO {
  children?: LocationTreeNode[];
}

export const addExpandArrowFlag = (node: LocationTreeNode): LocationTreeNode => {
  if (node.hasContainerNode) {
    node.extra = { ...(node.extra ?? {}), [EExtraFieldNames.ShowExpandArrow]: true };
  }
  return {
    ...node,
    children: node.children?.map((child) => addExpandArrowFlag(child)),
  };
};

export const pruneNode = (
  node: LocationTreeNode,
  searchText: string,
): LocationTreeNode | null => {
  if (!searchText) {
    return node;
  }
  const matchesSelf = (node.name ?? '').toLowerCase().includes(searchText);
  if (matchesSelf) {
    return node;
  }

  const prunedChildren = (node.children ?? [])
    .map((child) => pruneNode(child, searchText))
    .filter((child) => child != null);
  if (prunedChildren.length > 0) {
    return { ...node, children: prunedChildren };
  }

  return null;
};

export const ADVANCED_SEARCH_LOCATION_NODE_TYPES = [
  ETreeNodeTypes.Folder,
  ETreeNodeTypes.Project,
  ETreeNodeTypes.ProjectFolder,
  ETreeNodeTypes.ProjectsFolder,
  ETreeNodeTypes.Projects,
  ETreeNodeTypes.SearchesFolder,
  ETreeNodeTypes.Screens,
  ETreeNodeTypes.Searches,
  ETreeNodeTypes.ScreensFolder,
  ETreeNodeTypes.SynchrotronTripv4Folder,
  ETreeNodeTypes.CrystalTrackv4,
];

export const pruneToAllowedTypes = (
  node: LocationTreeNode,
  isRoot = false,
): LocationTreeNode | null => {
  const children = (node.children ?? [])
    .map((child) => pruneToAllowedTypes(child))
    .filter((child) => child != null);

  const isAllowedType = ADVANCED_SEARCH_LOCATION_NODE_TYPES.includes(
    node.type as ETreeNodeTypes,
  );
  if (!isRoot && !isAllowedType && children.length === 0) {
    return null;
  }

  return { ...node, children };
};

export const shouldRenderLocationNode = (
  node: LocationTreeNode,
  searchText: string,
): LocationTreeNode | null => {
  const transformed = addExpandArrowFlag(node);
  const typeFiltered = pruneToAllowedTypes(transformed, true);
  if (!typeFiltered) {
    return null;
  }
  return pruneNode(typeFiltered, (searchText ?? '').trim().toLowerCase());
};

// HACK: Utility func for fitting as much target chips as possible into given availableWidthPx
// Would no longer be needed once we've upgraded fmlx-common-ui
export const computeVisibleChipCount = (
  labels: string[],
  measureLabelWidth: (label: string) => number,
  chipOverheadPx: number,
  gapPx: number,
  availableWidthPx: number,
  moreIndicatorWidthPx: number,
): number => {
  let usedWidth = 0;
  let count = 0;

  for (let i = 0; i < labels.length; i += 1) {
    const chipWidth = measureLabelWidth(labels[i]) + chipOverheadPx;
    const gap = i > 0 ? gapPx : 0;
    const hasMoreAfter = i < labels.length - 1;
    const reserve = hasMoreAfter ? moreIndicatorWidthPx : 0;

    if (usedWidth + gap + chipWidth + reserve > availableWidthPx && count > 0) {
      break;
    }

    usedWidth += gap + chipWidth;
    count += 1;
  }

  return count;
};
